import { addDatabaseChangeListener } from "@-/com.npmjs.expo-sqlite";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { createOrReviveActorRow } from "@-/dev.improve.haniverse.database.actor/create-or-revive";
import { ACTOR_USER_ID } from "@-/dev.improve.haniverse.database.schemas.actor.identity";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { useActorTableChangeVersion } from "@-/dev.improve.haniverse.lib.database.change.actor";
import nullthrows from "@-/com.npmjs.expo__nullthrows";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useEffect } from "react";
import { setActorUserCache } from "#actor-user-cache";

/**
 * Hook returning a `queryOptions` factory for the actor's singleton `user` row
 * (which now carries the per-user settings columns).
 *
 * This is the async source of truth: query/mutation functions `fetchQuery` it to
 * always read the freshest DB-backed row. Render-time consumers read the
 * synchronous MMKV cache via `useActorUser()` instead — the cache is seeded here
 * (on resolve) and kept fresh by {@link useActorUserCacheSync}.
 *
 * The query key is registered for `user` writes so `fetchQuery` re-fetches live
 * as the record changes.
 */
export function useGetActorUserDataQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const userQueryFn = actorQueries(import.meta.location).user.findFirst.queryFn;
  const getUserQueryKey = actorQueries(import.meta.location).user.findFirst.useGetQueryKey({});
  const withTableChangeVersion = useActorTableChangeVersion("user", {});

  return () =>
    withTableChangeVersion(["actorUserData", { actorDb, getUserQueryKey, userQueryFn }] as const);
}

export const [useActorUserDataQueryOptions, actorUserDataQueryFn] = createUseQueryOptions(
  useGetActorUserDataQueryKey,
  async ({ client, queryKey: [_, { actorDb, getUserQueryKey, userQueryFn }] }) => {
    const fetchUser = (config: Parameters<typeof getUserQueryKey>[0]) =>
      client.fetchQuery({ queryFn: userQueryFn, queryKey: getUserQueryKey(config) });

    // The `user` row always has the id `ACTOR_USER_ID`, so it is read by primary key. The scan
    // only finds a row that came from elsewhere (an old export, a hand-built fixture).
    const existingUser =
      (await fetchUser({ where: { id: ACTOR_USER_ID } })) ?? (await fetchUser({}));
    if (existingUser !== null) {
      // Hydrate the sync MMKV cache so render-time `useActorUser()` reads
      // reactively replace their constructor-default snapshot.
      setActorUserCache(existingUser);
      return { user: existingUser };
    }

    // Fresh install: create the singleton `user` row (settings columns default
    // in the DDL). Every device creates this same row, so two of them never
    // produce two users. The Favorites collection is created by the first star.
    await createOrReviveActorRow(actorDb(import.meta.location), actorSchema.user, {
      createdAt: new Date().toISOString(),
      id: ACTOR_USER_ID,
    });
    const user = nullthrows(await fetchUser({ where: { id: ACTOR_USER_ID } }));
    setActorUserCache(user);
    return { user };
  },
);

/**
 * Keeps the synchronous MMKV actor-user cache in step with the DB.
 *
 * Subscribes to committed `user` writes (via the low-level
 * `addDatabaseChangeListener`) and re-reads the row into the cache, so a settings
 * toggle (local) or a synced write (remote) flows through to every `useActorUser()`
 * consumer. Mount it ONCE, high in the `(app)` tree (see `AppLayoutSuspense`).
 *
 * The change event fires after the write commits, so the async re-read always
 * sees the new value; the cache lands a tick later (sync reads serve the previous
 * value until then), which is the accepted read-after-write latency for this
 * pattern.
 */
export function useActorUserCacheSync() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);

  useEffect(() => {
    let alive = true;
    const subscription = addDatabaseChangeListener((event) => {
      if (event.tableName !== "user") {
        return;
      }
      void (async () => {
        const user = await actorDb(import.meta.location).query.user.findFirst();
        if (alive && user !== undefined) {
          setActorUserCache(user);
        }
      })();
    });
    return () => {
      alive = false;
      subscription.remove();
    };
  }, [actorDb]);
}
